import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../../services/ApiService';
import { cn } from '../../lib/utils';

interface Model {
    id: number;
    name: string;
    model_scenarios: { id: number; name: string; is_computed: boolean }[];
}

interface Company {
    model_data_id: number;
    name: string;
}

interface PickerResult {
    model_id: number;
    model_name: string;
    scenario_id: number;
    scenario_name: string;
    company_model_data_id: number;
    company_name: string;
}

interface Props {
    onSelect: (result: PickerResult) => void;
    onCancel: () => void;
    disabledCompanyIds?: number[];
}

export default function ModelScenarioPicker({ onSelect, onCancel, disabledCompanyIds = [] }: Props) {
    const disabledCompanySet = useMemo(() => new Set(disabledCompanyIds), [disabledCompanyIds]);
    const [models, setModels] = useState<Model[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    // Selection state
    const [selectedModel, setSelectedModel] = useState<Model | null>(null);
    const [selectedScenario, setSelectedScenario] = useState<any>(null);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    // Load models from folder API
    useEffect(() => {
        const fetchModels = async () => {
            setLoading(true);
            try {
                const response = await apiService.getRootFolder();
                if (response && response.ok) {
                    const data = await response.json();
                    // data contains { models: [...], shared_models: [...] }
                    const allModels = [...(data.models || []), ...(data.shared_models || [])];
                    // Deduplicate by ID
                    const seen = new Set<number>();
                    const unique = allModels.filter((m: Model) => {
                        if (seen.has(m.id)) return false;
                        seen.add(m.id);
                        return true;
                    });
                    setModels(unique);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchModels();
    }, []);

    // Fetch companies when scenario is selected
    useEffect(() => {
        if (!selectedModel || !selectedScenario) {
            setCompanies([]);
            setSelectedCompany(null);
            return;
        }
        const fetchCompanies = async () => {
            setLoadingCompanies(true);
            try {
                const response = await apiService.getAvailableCompanies(selectedModel.id, selectedScenario.id);
                if (response && response.ok) {
                    const data = await response.json();
                    setCompanies(data);
                    if (data.length === 1 && !disabledCompanySet.has(data[0].model_data_id)) {
                        setSelectedCompany(data[0]);
                    }
                }
            } finally {
                setLoadingCompanies(false);
            }
        };
        fetchCompanies();
    }, [selectedModel?.id, selectedScenario?.id]);

    const filteredModels = useMemo(() => {
        if (!search.trim()) return models;
        const q = search.toLowerCase();
        return models.filter(m => m.name.toLowerCase().includes(q));
    }, [models, search]);

    const handleConfirm = () => {
        if (!selectedModel || !selectedScenario || !selectedCompany) return;
        onSelect({
            model_id: selectedModel.id,
            model_name: selectedModel.name,
            scenario_id: selectedScenario.id,
            scenario_name: selectedScenario.name,
            company_model_data_id: selectedCompany.model_data_id,
            company_name: selectedCompany.name,
        });
    };

    // Step indicator
    const currentStep = !selectedModel ? 1 : !selectedScenario ? 2 : !selectedCompany ? 3 : 4;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="p-5 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">Select Asset</h3>
                    <div className="flex items-center gap-2 mt-2">
                        {[1, 2, 3].map((step) => (
                            <React.Fragment key={step}>
                                <div className={cn(
                                    'flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold',
                                    currentStep > step ? 'bg-blue-600 text-white' :
                                    currentStep === step ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-600' :
                                    'bg-slate-100 text-slate-400'
                                )}>
                                    {currentStep > step ? (
                                        <span className="material-symbols-outlined text-[14px]">check</span>
                                    ) : step}
                                </div>
                                <span className={cn('text-xs', currentStep >= step ? 'text-slate-700' : 'text-slate-400')}>
                                    {step === 1 ? 'Model' : step === 2 ? 'Scenario' : 'Company'}
                                </span>
                                {step < 3 && <span className="text-slate-300">›</span>}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-4">
                    {/* Step 1: Model selection */}
                    {!selectedModel && (
                        <>
                            <input
                                type="text"
                                placeholder="Search models..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                                autoFocus
                            />
                            {loading ? (
                                <div className="flex justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : filteredModels.length === 0 ? (
                                <p className="text-center py-8 text-slate-400 text-sm">No models found</p>
                            ) : (
                                <div className="space-y-1 max-h-[300px] overflow-auto">
                                    {filteredModels.map((model) => (
                                        <button
                                            key={model.id}
                                            onClick={() => setSelectedModel(model)}
                                            className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-blue-50 hover:text-blue-900 transition-colors text-sm"
                                        >
                                            <div className="font-medium">{model.name}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                {model.model_scenarios?.length || 0} scenario{(model.model_scenarios?.length || 0) !== 1 ? 's' : ''}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Step 2: Scenario selection */}
                    {selectedModel && !selectedScenario && (
                        <>
                            <button
                                onClick={() => { setSelectedModel(null); setSearch(''); }}
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mb-3"
                            >
                                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                Back to models
                            </button>
                            <p className="text-sm text-slate-500 mb-2">Select a scenario for <span className="font-semibold text-slate-700">{selectedModel.name}</span></p>
                            {selectedModel.model_scenarios?.length === 0 ? (
                                <p className="text-center py-8 text-slate-400 text-sm">No scenarios in this model</p>
                            ) : (
                                <div className="space-y-1">
                                    {selectedModel.model_scenarios.map((scenario: any) => (
                                        <button
                                            key={scenario.id}
                                            onClick={() => setSelectedScenario(scenario)}
                                            className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-blue-50 hover:text-blue-900 transition-colors text-sm"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium">{scenario.name}</span>
                                                {scenario.is_computed && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                                        Computed
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Step 3: Company selection */}
                    {selectedModel && selectedScenario && !selectedCompany && (
                        <>
                            <button
                                onClick={() => { setSelectedScenario(null); setSelectedCompany(null); }}
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mb-3"
                            >
                                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                Back to scenarios
                            </button>
                            <p className="text-sm text-slate-500 mb-2">
                                Select a company in <span className="font-semibold text-slate-700">{selectedScenario.name}</span>
                            </p>
                            {loadingCompanies ? (
                                <div className="flex justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : companies.length === 0 ? (
                                <p className="text-center py-8 text-slate-400 text-sm">No companies found in this scenario</p>
                            ) : (
                                <div className="space-y-1">
                                    {companies.map((company) => {
                                        const isDisabled = disabledCompanySet.has(company.model_data_id);
                                        return (
                                            <button
                                                key={company.model_data_id}
                                                onClick={() => setSelectedCompany(company)}
                                                disabled={isDisabled}
                                                title={isDisabled ? 'Already in this portfolio group' : undefined}
                                                className={cn(
                                                    'w-full text-left px-3 py-2.5 rounded-lg transition-colors text-sm font-medium flex items-center justify-between',
                                                    isDisabled
                                                        ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                                                        : 'hover:bg-blue-50 hover:text-blue-900'
                                                )}
                                            >
                                                <span>{company.name}</span>
                                                {isDisabled && (
                                                    <span className="text-xs font-normal text-slate-400">Already added</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* Step 4: Confirmation */}
                    {selectedModel && selectedScenario && selectedCompany && (
                        <div className="space-y-3">
                            <p className="text-sm text-slate-500 mb-3">Confirm your selection:</p>
                            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Model</span>
                                    <span className="font-medium text-slate-900">{selectedModel.name}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Scenario</span>
                                    <span className="font-medium text-slate-900">{selectedScenario.name}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Company</span>
                                    <span className="font-medium text-slate-900">{selectedCompany.name}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => { setSelectedCompany(null); }}
                                className="text-sm text-blue-600 hover:text-blue-700"
                            >
                                Change selection
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedModel || !selectedScenario || !selectedCompany}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Select
                    </button>
                </div>
            </div>
        </div>
    );
}
